import {Component, inject, OnInit} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {TranslateService} from '@ngx-translate/core';
import {ProgressService} from '../../../shared/services/progress.service';
import {SnackbarService} from '../../../shared/services/snackbar.service';
import {ConfigRouteEnum} from "../../../shared/enums/config-route.enum";
import {QueryDataService} from "../services/query-data.service";
import {ConfigOverviewModel} from "../../../shared/components/models/config-overview.model";
import {ConfigCardModel} from "../../../shared/models/config-card.model";
import {ConfigTableDisplayedColumns} from "../../../shared/components/models/config-table.model";
import {QueryOverview} from "../models/query.model";
import {finalize, map, of, tap} from "rxjs";
import {catchError} from "rxjs/operators";
import {ConfigButton} from "../../../shared/components/models/config-button";
import {ConfigOverviewComponent} from "../../../shared/components/config-overview/config-overview.component";
import {QuerySearch} from "../../../shared/services/models/query-service.model";

@Component({
    selector: 'app-query-overview',
    templateUrl: './query-overview.component.html',
    styleUrls: ['./query-overview.component.scss'],
    standalone: true,
    imports: [
        ConfigOverviewComponent,
    ]
})
export class QueryOverviewComponent implements OnInit {
    queryModel?: ConfigOverviewModel;

    private readonly _router: Router = inject(Router);

    private allCards: ConfigCardModel[] = [];
    private displayedColumns: any[] = [];
    private products: QueryOverview[] = [];

    private readonly queryDataService = inject(QueryDataService);
    private readonly progress = inject(ProgressService);
    private readonly snackBar = inject(SnackbarService);
    private readonly router = inject(Router);
    private readonly activatedRoute = inject(ActivatedRoute);
    protected readonly ConfigRouteEnum = ConfigRouteEnum;
    private readonly translate = inject(TranslateService);

    ngOnInit(): void {
        this.loadQueries();
    }

    private loadQueries(): void {
        const appName = this.activatedRoute.snapshot.params['searchName'];

        this.progress.startLoading();

        this.queryDataService.searchQuery(appName, '')
            .pipe(
                map((queries: QuerySearch[]) => {
                    this.products = queries.map((query: QuerySearch) => {
                        const id = (query as any)._links?.self?.href?.split('/').pop();
                        return {
                            name: query.queryName,
                            searchName: id,
                            queryVersion: query.queryVersion,
                            version: query.applicationVersion
                        };
                    }) as QueryOverview[];
                }),
                tap(() => this.buildOverview()),
                catchError(() => {
                    this.snackBar.Error(this.translate.instant("snackbar.query.error"));
                    this.products = [];
                    this.buildOverview();
                    return of([]);
                }),
                finalize(() => this.progress.stopLoading())
            )
            .subscribe();
    }

    private buildOverview(): void {
        const appName = this.activatedRoute.snapshot.params['searchName'];
        this.allCards = this.products.map(
            (t, id) => {
                const card = new ConfigCardModel({
                    id: id,
                    title: t.name,
                    searchName: t.searchName,
                    body: null,
                    logoUrl: null,
                    route: {
                        commands: [ConfigRouteEnum.QUERY, appName, t.searchName],
                    },
                    badges: [
                        {amount: t.queryVersion, label: 'query.queryVersion', routerLink: undefined},
                        {amount: t.version, label: 'query.appVersion', routerLink: undefined}
                    ],
                });
                (card as any).queryVersion = t.queryVersion;
                (card as any).version = t.version;
                return card;
            }
        );

        this.displayedColumns = ConfigTableDisplayedColumns.build(
            {HeaderCellName: 'query.queries', PropertyName: 'title'},
            {HeaderCellName: 'query.queryVersion', PropertyName: 'queryVersion'},
            {HeaderCellName: 'query.appVersion', PropertyName: 'version'}
        );

        this.queryModel = {
            title: appName || 'query.queries',
            buttons: ConfigButton.build({
                text: 'query.create',
                icon: 'pi pi-plus',
                onClick: async () => await this.router.navigate(['/', ConfigRouteEnum.QUERY, 'new'])
            }),
            cards: this.allCards,
            table: {
                settings: {
                    header: true,
                    headerAction: true,
                    search: true,
                },
                data: this.allCards,
                displayedColumns: this.displayedColumns,
                onRowClick: (row: any) => this._router.navigate(row.route.commands, row.route.extras),
            },
        };
    }
}
